import { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, useSearchParams, Link } from "react-router-dom";
import { Toaster } from "sonner";

import { I18nProvider } from "@/lib/i18n";
import { LocationPrimerProvider } from "@/lib/LocationPrimer";
import { WhatsAppSheetProvider } from "@/components/WhatsAppActionSheet";
import { ContactPopupHost } from "@/components/ContactPopup";
import { Header } from "@/components/Header";
import CustomCodeInjector from "@/components/CustomCodeInjector";
import { getMe } from "@/lib/api";
import { lsGet, lsSet, lsRemove } from "@/lib/safeStorage";

import Splash from "@/pages/Splash";
import AuthCallback from "@/pages/AuthCallback";
import SelectRole from "@/pages/SelectRole";
import TravelerSignup from "@/pages/TravelerSignup";
import Welcome from "@/pages/Welcome";
import Home from "@/pages/Home";
import Places from "@/pages/Places";
import PlaceProfile from "@/pages/PlaceProfile";
import CustomLocationPublic from "@/pages/CustomLocationPublic";
import CustomLocationDetail from "@/pages/CustomLocationDetail";
import CustomLocationForm from "@/pages/CustomLocationForm";
import MySaved from "@/pages/MySaved";
import MyFavorites from "@/pages/MyFavorites";
import RidesHistory from "@/pages/RidesHistory";
import RideDetail from "@/pages/RideDetail";
import ActiveRide from "@/pages/ActiveRide";
import RideCustomerScan from "@/pages/RideCustomerScan";
import TipDetail from "@/pages/TipDetail";
import RideTrackingProvider from "./components/RideTrackingProvider";
import MyPlaces from "@/pages/MyPlaces";
import MyVendors from "@/pages/MyVendors";
import { PreviewModeProvider, PreviewBanner, PreviewBannerSpacer } from "@/admin/PreviewMode";
import Scan from "@/pages/Scan";
const Journey = lazy(() => import("@/pages/Journey"));
const MapScreen = lazy(() => import("@/pages/Map"));
import Settings from "@/pages/Settings";
import ApplicationSubmitted from "@/pages/ApplicationSubmitted";
const ApplicationForm = lazy(() => import("@/pages/ApplicationForm"));
import Friends from "@/pages/Friends";
import FriendProfile from "@/pages/FriendProfile";
import MyProfile from "@/pages/MyProfile";
import MyQr from "@/pages/MyQr";
import Suggest from "@/pages/Suggest";
import Tips from "@/pages/Tips";
import ExchangeRatePage from "@/pages/ExchangeRate";
import EmailFeedback from "@/pages/EmailFeedback";
import PrivacySettings from "@/pages/PrivacySettings";
import Notifications from "@/pages/Notifications";
const Airport = lazy(() => import("@/pages/Airport"));
import Install from "@/pages/Install";
import OfflineInstall from "@/pages/OfflineInstall";
import Tonight from "@/pages/Tonight";
import Car from "@/pages/Car";
import CarMap from "@/pages/CarMap";
import ScrollResetOnRouteChange from "@/components/ScrollResetOnRouteChange";
import ScrollToTopButton from "@/components/ScrollToTopButton";
import { UpdatesList, UpdateDetail } from "@/pages/Updates";
import { OfflineIndicator } from "@/components/OfflineIndicator";
const Foundation = lazy(() => import("@/pages/Foundation"));
const Pipeline = lazy(() => import("@/pages/Pipeline"));
import { ReviewToastPoller } from "@/components/ReviewToastPoller";
const PublicOnboarding = lazy(() => import("@/pages/PublicOnboarding"));
import AdminLayout from "@/admin/AdminLayout";
/* iter189 — Code splitting: admin, business, vendor, and secondary
   pages are lazy-loaded so anon users on cold-load never download
   their bundles. Each route below is wrapped in <Suspense>. */
const AdminOverview = lazy(() => import("@/admin/AdminOverview"));
const AdminBusinesses = lazy(() => import("@/admin/AdminBusinesses"));
const AdminBusinessDetail = lazy(() => import("@/admin/AdminBusinessDetail"));
const AdminCategories = lazy(() => import("@/admin/AdminCategories"));
const AdminRideModeBackgrounds = lazy(() => import("@/admin/AdminRideModeBackgrounds"));
const AdminTransport = lazy(() => import("@/admin/AdminTransport"));
const AdminMedia = lazy(() => import("@/admin/AdminMedia"));
const AdminPush = lazy(() => import("@/admin/AdminPush"));
const Alerts = lazy(() => import("@/pages/Alerts"));
const AdminApplications = lazy(() => import("@/admin/AdminApplications"));
const AdminJobs = lazy(() => import("@/admin/AdminJobs"));
const AdminVendorCategories = lazy(() => import("@/admin/AdminVendorCategories"));
const AdminApplicationDetail = lazy(() => import("@/admin/AdminApplications").then(m => ({ default: m.AdminApplicationDetail })));
const AdminVendors = lazy(() => import("@/admin/AdminVendors"));
const AdminVendorDetail = lazy(() => import("@/admin/AdminVendorDetail"));
const AdminReviews = lazy(() => import("@/admin/AdminReviews"));
const AdminReviewDetail = lazy(() => import("@/admin/AdminReviewDetail"));
const AdminBanners = lazy(() => import("@/admin/AdminBanners"));
const AdminUpdates = lazy(() => import("@/admin/AdminUpdates"));
const AdminTips = lazy(() => import("@/admin/AdminTips"));
const AdminExchangeRate = lazy(() => import("@/admin/AdminExchangeRate"));
const AdminWhatsApp = lazy(() => import("@/admin/AdminWhatsApp"));
const AdminWhatsAppActivity = lazy(() => import("@/admin/AdminWhatsAppActivity"));
const AdminDataCleanup = lazy(() => import("@/admin/AdminDataCleanup"));
const AdminEmailSettings = lazy(() => import("@/admin/AdminEmailSettings"));
const AdminFeatured = lazy(() => import("@/admin/AdminFeatured"));
const AdminAudit = lazy(() => import("@/admin/AdminAudit"));
const AdminData = lazy(() => import("@/admin/AdminData"));
const AdminTranslations = lazy(() => import("@/admin/AdminTranslations"));
import {
  AdminRecommendationTemplates,
  AdminRecommendationLog,
  AdminVendorInteractions,
} from "@/admin/AdminRecommendations";
const AdminUsers = lazy(() => import("@/admin/AdminUsers"));
const AdminSettings = lazy(() => import("@/admin/AdminSettings"));
const AdminAudienceSettings = lazy(() => import("@/admin/AdminAudienceSettings"));
const AdminWhatsAppCloud = lazy(() => import("@/admin/AdminWhatsAppCloud"));
import EmailVerify from "@/pages/EmailVerify";
import WaVerify from "@/pages/WaVerify";
const AdminSupport = lazy(() => import("@/admin/AdminSupport"));
const AdminTermsAcceptancePage = lazy(() => import("@/admin/AdminTermsAcceptancePage"));
const AdminSharedLocations = lazy(() => import("@/admin/AdminSharedLocations"));
const AdminSharedLocationDetail = lazy(() => import("@/admin/AdminSharedLocationDetail"));
import ConnectedPage from "@/pages/Connected";
import { MyTicketsPage, TicketDetailPage } from "@/pages/SupportTickets";
import BusinessLayout from "@/business/BusinessLayout";
const BusinessOverview = lazy(() => import("@/business/BusinessOverview"));
const BusinessVisitors = lazy(() => import("@/business/BusinessVisitors"));
const BusinessQR = lazy(() => import("@/business/BusinessQR"));
const BusinessProfile = lazy(() => import("@/business/BusinessProfile"));
const BusinessAnalytics = lazy(() => import("@/business/BusinessAnalytics"));
const BusinessGuestBook = lazy(() => import("@/business/BusinessGuestBook"));
import VendorLayout from "@/vendor/VendorLayout";
const VendorQR = lazy(() => import("@/vendor/VendorQR"));
const VendorReviews = lazy(() => import("@/vendor/VendorReviews"));
const VendorReactivate = lazy(() => import("@/vendor/VendorReactivate"));
const VendorProfile = lazy(() => import("@/vendor/VendorProfile"));
const VendorGuestBook = lazy(() => import("@/vendor/VendorGuestBook"));
const VendorAnalytics = lazy(() => import("@/vendor/VendorAnalytics"));
const VendorOverview = lazy(() => import("@/vendor/VendorOverview"));
const VendorJobs = lazy(() => import("@/vendor/VendorJobs"));
const VendorMessages = lazy(() => import("@/vendor/VendorMessages"));
const VendorMessageForm = lazy(() => import("@/pages/VendorMessageForm"));
const RideBidReview = lazy(() => import("@/pages/RideBidReview"));
import VendorPublic from "@/pages/VendorPublic";
import Join from "@/pages/Join";
import GetListedSheet from "@/components/GetListedSheet";
import DesktopHolding from "@/pages/DesktopHolding";
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
import Share from "@/pages/Share";
import LanguagePicker from "@/components/LanguagePicker";
import WelcomePopup from "@/components/WelcomePopup";
import ErrorBoundary from "@/components/ErrorBoundary";
import LegalFooter from "@/components/LegalFooter";
import TermsReacceptOverlay from "@/components/TermsReacceptOverlay";

/** Tracks viewport so we can swap between mobile app + desktop holding page. */
function useIsDesktop(breakpoint = 1024) {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isDesktop;
}

// Mobile-app routes manage their own chrome (no global header/footer).
const MOBILE_PATHS = [
  "/", "/places", "/places/", "/scan", "/map", "/journey",
  "/settings/profile", "/settings/privacy", "/settings", "/signup/traveler",
  "/friends", "/tips", "/my-qr", "/suggest", "/notifications", "/alerts", "/vendors",
  "/join", "/exchange-rate", "/airport", "/install", "/tonight", "/car", "/welcome", "/select-role", "/connected", "/support",
  "/me", "/my-places", "/my-favorites", "/my-vendors", "/saved",
  "/rides", "/ride", "/offline",
];

function isMobileRoute(pathname) {
  if (pathname === "/") return true;
  return MOBILE_PATHS.some((p) => p !== "/" && (pathname === p || pathname.startsWith(p + "/")));
}

// iter190 — /join is now the "Grow Your Business With Habano" marketing
// page (see pages/JoinPage.jsx). The share landing at /join?type=…&id=…
// still routes to pages/Join.jsx so QR/WhatsApp share URLs keep working.
const JoinPage = lazy(() => import("@/pages/JoinPage"));

function JoinOrGetListed() {
  const [params] = useSearchParams();
  if (params.get("type") || params.get("id")) return <Join />;
  return <JoinPage />;
}

function IndexRouter() {
  const [state, setState] = useState("loading");
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;

    // Promote ?guest=1 to localStorage so the flag survives refresh.
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("guest") === "1") {
        lsSet("habano.guest", "1");
        // Strip the query so we don't leak it across navigation.
        window.history.replaceState(null, "", window.location.pathname);
      }
    } catch { /* ignore */ }

    let cancelled = false;
    (async () => {
      let me = null;
      try { me = await getMe(); } catch { me = null; }
      if (cancelled) return;

      if (!me) {
        // Guest mode: tapped "Continue as Guest" before → go straight to Home.
        // Anon visitors land directly in the app — no marketing splash.
        const isGuest = lsGet("habano.guest") === "1";
        setState(isGuest ? "guest" : "anon");
        return;
      }
      // Authenticated user — clear any stale guest flag.
      lsRemove("habano.guest");
      // iter170 — Admins are NOT auto-redirected to /admin. Being
      // flagged admin only grants access to the admin dashboard (and
      // to Discover when the global switch is OFF) — they continue
      // using the app normally and can navigate to /admin themselves
      // when they choose to.
      if (me.role === "business") { navigate("/business"); return; }
      if (me.role === "vendor") { navigate("/vendor"); return; }
      setUser(me);
      setState("authed");
    })();

    // Safety net — if the auth probe stalls (slow network, hung fetch),
    // never leave the user staring at a black `null` render. Settle to a
    // sensible default after 4s.
    const stallTimer = setTimeout(() => {
      if (cancelled) return;
      setState((curr) => {
        if (curr !== "loading") return curr;
        const isGuest = lsGet("habano.guest") === "1";
        return isGuest ? "guest" : "anon";
      });
    }, 4000);

    return () => { cancelled = true; clearTimeout(stallTimer); };
  }, [navigate]);

  if (state === "loading") {
    return (
      <main data-testid="index-router-loading" className="min-h-screen grid place-items-center" style={{ background: "#0B0B0B" }}>
        <div className="flex flex-col items-center gap-5">
          <img
            src="/icons/icon-180x180.png"
            alt="Habano App"
            width={180}
            height={180}
            style={{ width: 180, height: 180, borderRadius: 28, animation: "habanoSplashPulse 2.4s ease-in-out infinite" }}
          />
          <div
            style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 18,
              color: "#E8B84B",
              letterSpacing: "0.06em",
            }}
          >
            Habano App
          </div>
        </div>
      </main>
    );
  }
  // Desktop visitors get the holding page; marketing site lives on WordPress.
  if (isDesktop) return <DesktopHolding />;
  // Anon visitors land directly in the app — Home renders the onboarding
  // overlay on first visit via the `habano.onboarding.dismissed` flag.
  return <Home user={user} />;
}


/* iter189 — FirstVisitOnboarding removed. The old first-visit overlay
 * loaded a 2.3MB havana-night.jpg background image that dominated the
 * cold-load payload. Anon users now go straight to Discover; the
 * onboarding content lives on /welcome and /join for users who want it. */

function StubPage({ title, blurb, testid }) {
  return (
    <main className="min-h-screen pt-28 pb-28 px-6 bg-background" data-testid={testid}>
      <div className="max-w-md mx-auto text-center">
        <div className="eyebrow">Coming next</div>
        <h1 className="font-display text-4xl mt-3 text-foreground">{title}</h1>
        <p className="mt-3 text-foreground/55 text-sm">{blurb}</p>
      </div>
    </main>
  );
}

/* iter189 — Lightweight route fallback for code-split chunks. Matches
   the loading-state background so there's no white flash while a lazy
   chunk downloads. Chunks are cached forever after first fetch. */
function RouteFallback() {
  return (
    <main
      data-testid="route-fallback"
      className="min-h-screen grid place-items-center"
      style={{ background: "#0B0B0B" }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 40, height: 40, borderRadius: 999,
          border: "2px solid rgba(212,166,76,0.15)",
          borderTopColor: "#E8B84B",
          animation: "habanoSpin 900ms linear infinite",
        }}
      />
      <style>{`@keyframes habanoSpin { to { transform: rotate(360deg); } }`}</style>
    </main>
  );
}


function Shell() {
  const location = useLocation();
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    (async () => {
      const me = await getMe();
      setUser(me);
    })();
  }, [location.pathname]);

  useEffect(() => {
    // iter103 — Refresh the cached `whatsapp_auth_enabled` admin setting on
    // every app load so the LoginSheet + WhatsAppActionSheet always honour
    // the admin's current toggle without needing a Settings page visit.
    import("@/lib/waAuth").then((m) => m.refreshWaAuthEnabled()).catch(() => {});
    // iter104 — Same pattern for generic boolean feature flags
    // (log_spend_enabled, etc.).
    import("@/lib/featureFlags").then((m) => m.refreshFeatureFlags()).catch(() => {});
    // iter170 — Refresh the Discover-availability cache (global toggle +
    // per-user admin bypass) so all gating renders reactively as soon
    // as the network responds.
    import("@/lib/discoverAvailability").then((m) => m.refreshDiscoverAvailability()).catch(() => {});
    // iter122 — Silent global cache warmer.
    import("@/lib/businessCache").then((m) => m.warmGlobalCache()).catch(() => {});
    // iter154 — Warm the category hierarchy cache so FilterPlacesSheet
    // and business cards can render L1 · L2 labels offline.
    import("@/lib/categoryHierarchy").then((m) => m.warmCategoryCache()).catch(() => {});
    // iter129 — Flush any queued application submission once we're online,
    // and re-flush whenever connectivity recovers mid-session.
    const flushApps = () => {
      import("@/lib/applications").then((m) => m.flushPendingApplication()).catch(() => {});
    };
    flushApps();
    window.addEventListener("online", flushApps);
    return () => window.removeEventListener("online", flushApps);
  }, []);

  useEffect(() => {
    // Global PWA install celebration — fires once when the browser confirms install.
    // Works on Chrome/Android/Edge (and Safari iOS 16.4+ which now dispatches the event).
    const onInstalled = () => {
      try {
        // Lazy-load toast to avoid bundle bloat in this top-level shell
        import("sonner").then(({ toast }) => {
          toast.success("Welcome to Habano · Tap the icon on your home screen anytime");
        });
        localStorage.setItem("habano.welcome.never", "true");
      } catch { /* ignore */ }
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  const isAdmin = location.pathname.startsWith("/admin");
  const isBusiness = location.pathname.startsWith("/business");
  const isVendor = location.pathname.startsWith("/vendor");
  const isMarketing = location.pathname === "/" || location.pathname === "/privacy" || location.pathname === "/terms" || location.pathname === "/share";
  const isMobile = isMobileRoute(location.pathname);
  const showChrome = !isAdmin && !isBusiness && !isVendor && !isMarketing && !isMobile;

  return (
    <>
      <ScrollResetOnRouteChange />
      <ScrollToTopButton />
      <OfflineIndicator />
      {showChrome && <Header user={user} />}
      <PreviewBanner />
      <PreviewBannerSpacer />
      <LanguagePicker />
      <WelcomePopup />
      {user && <ReviewToastPoller />}
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<IndexRouter />} />
        <Route path="/login" element={<Splash />} />
        <Route path="/select-role" element={<SelectRole />} />
        <Route path="/signup/traveler" element={<TravelerSignup />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/share" element={<Share />} />
        <Route path="/places" element={<Places />} />
        <Route path="/places/custom/:id" element={<CustomLocationPublic />} />
        <Route path="/places/:id" element={<PlaceProfile />} />
        {/* iter140 — Singular alias for direct QR-code deep links. External
            QR codes printed for businesses use the canonical /place/<id>
            URL (singular); without this route a cold load fell through to
            no match. Renders the same component as /places/:id. */}
        <Route path="/place/:id" element={<PlaceProfile />} />
        {/* iter129 — Get Listed application forms (sheet trigger lives in ProfileMenu) */}
        <Route path="/get-listed/business" element={<ApplicationForm kind="business" />} />
        <Route path="/get-listed/vendor" element={<ApplicationForm kind="vendor" />} />
        <Route path="/apply/submitted/:kind" element={<ApplicationSubmitted />} />
        <Route path="/my-places/custom" element={<MyPlaces />} />
        <Route path="/my-places/custom/new" element={<CustomLocationForm />} />
        <Route path="/my-places/custom/:id" element={<CustomLocationDetail />} />
        <Route path="/my-places/custom/:id/edit" element={<CustomLocationForm />} />
        <Route path="/saved" element={<MySaved />} />
        <Route path="/my-favorites" element={<MyFavorites />} />
        <Route path="/my-vendors" element={<MyVendors />} />
        <Route path="/vendors/:id" element={<VendorPublic />} />
        <Route path="/v/:vid/message" element={<VendorMessageForm />} />
        <Route path="/ride/bids/:jid" element={<RideBidReview />} />
        <Route path="/join" element={<JoinOrGetListed />} />
        <Route path="/scan" element={<Scan />} />
        <Route path="/map" element={<MapScreen />} />
        <Route path="/exchange-rate" element={<ExchangeRatePage />} />
        <Route path="/connected" element={<ConnectedPage />} />
        <Route path="/support/tickets" element={<MyTicketsPage />} />
        <Route path="/support/tickets/:ticketId" element={<TicketDetailPage />} />
        <Route path="/email-feedback" element={<EmailFeedback />} />
        <Route path="/journey" element={<Journey />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/profile" element={<Settings />} />
        <Route path="/settings/privacy" element={<PrivacySettings />} />
        <Route path="/friends" element={<Friends />} />
        <Route path="/friends/:userId" element={<FriendProfile />} />
        <Route path="/me/profile" element={<MyProfile />} />
        <Route path="/my-qr" element={<MyQr />} />
        <Route path="/suggest" element={<Suggest />} />
        <Route path="/tips" element={<Tips />} />
        <Route path="/tips/:id" element={<TipDetail />} />
        <Route path="/airport" element={<Airport />} />
        <Route path="/install" element={<Install />} />
        <Route path="/offline" element={<OfflineInstall />} />
        <Route path="/tonight" element={<Tonight />} />
        <Route path="/car" element={<Car />} />
        <Route path="/car/map" element={<CarMap />} />
        <Route path="/rides/history" element={<RidesHistory />} />
        <Route path="/rides/:rideId" element={<RideDetail />} />
        <Route path="/ride/active/:rideId" element={<ActiveRide />} />
        <Route path="/ride/customer/:rideId" element={<RideCustomerScan />} />
        <Route path="/updates" element={<UpdatesList />} />
        <Route path="/updates/:id" element={<UpdateDetail />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/alerts" element={<Alerts />} />
        <Route path="/foundation" element={<Foundation />} />
        <Route path="/pipeline" element={<Pipeline />} />
        <Route path="/onboarding/business" element={<PublicOnboarding />} />
        <Route path="/auth/email/verify" element={<EmailVerify />} />
        <Route path="/auth/verify" element={<WaVerify />} />
        <Route path="/auth/wa/verify" element={<WaVerify />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminOverview />} />
          <Route path="businesses" element={<AdminBusinesses />} />
          <Route path="businesses/:id" element={<AdminBusinessDetail />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="ride-mode-backgrounds" element={<AdminRideModeBackgrounds />} />
          <Route path="transport" element={<AdminTransport />} />
          <Route path="media" element={<AdminMedia />} />
          <Route path="push" element={<AdminPush />} />
          <Route path="applications" element={<AdminApplications />} />
          <Route path="applications/:aid" element={<AdminApplicationDetail />} />
          <Route path="jobs" element={<AdminJobs />} />
          <Route path="vendor-categories" element={<AdminVendorCategories />} />
          <Route path="vendors" element={<AdminVendors />} />
          <Route path="vendors/:id" element={<AdminVendorDetail />} />
          <Route path="reviews" element={<AdminReviews />} />
          <Route path="reviews/:id" element={<AdminReviewDetail />} />
          <Route path="banners" element={<AdminBanners />} />
          <Route path="updates" element={<AdminUpdates />} />
          <Route path="tips" element={<AdminTips />} />
          <Route path="exchange-rate" element={<AdminExchangeRate />} />
          <Route path="whatsapp" element={<AdminWhatsApp />} />
          <Route path="whatsapp-activity" element={<AdminWhatsAppActivity />} />
          <Route path="data-cleanup" element={<AdminDataCleanup />} />
          <Route path="email" element={<AdminEmailSettings />} />
          <Route path="featured" element={<AdminFeatured />} />
          <Route path="audit" element={<AdminAudit />} />
          <Route path="data" element={<AdminData />} />
          <Route path="translations" element={<AdminTranslations />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="support" element={<AdminSupport />} />
          <Route path="terms-acceptance" element={<AdminTermsAcceptancePage />} />
          <Route path="shared-locations" element={<AdminSharedLocations />} />
          <Route path="shared-locations/:id" element={<AdminSharedLocationDetail />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="audience-settings" element={<AdminAudienceSettings />} />
          <Route path="whatsapp-cloud" element={<AdminWhatsAppCloud />} />
          <Route path="recommendation-templates" element={<AdminRecommendationTemplates />} />
          <Route path="recommendations" element={<AdminRecommendationLog />} />
          <Route path="vendor-interactions" element={<AdminVendorInteractions />} />
        </Route>
        <Route path="/business" element={<BusinessLayout />}>
          <Route index element={<BusinessOverview />} />
          <Route path="visitors" element={<BusinessVisitors />} />
          <Route path="guest-book" element={<BusinessGuestBook />} />
          <Route path="qr" element={<BusinessQR />} />
          <Route path="profile" element={<BusinessProfile />} />
          <Route path="analytics" element={<BusinessAnalytics />} />
        </Route>
        <Route path="/vendor" element={<VendorLayout />}>
          <Route index element={<VendorOverview />} />
          <Route path="qr" element={<VendorQR />} />
          <Route path="reviews" element={<VendorReviews />} />
          <Route path="reactivate" element={<VendorReactivate />} />
          <Route path="profile" element={<VendorProfile />} />
          <Route path="guest-book" element={<VendorGuestBook />} />
          <Route path="analytics" element={<VendorAnalytics />} />
          <Route path="jobs" element={<VendorJobs />} />
          <Route path="messages" element={<VendorMessages />} />
        </Route>
      </Routes>
      </Suspense>
      {showChrome && (
        <footer className="border-t border-gold-soft py-8 mt-16 text-xs text-foreground/45">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="font-display tracking-wide">Habano.app · La Habana, Cuba · 2025</div>
            <div className="flex items-center gap-3 font-mono">
              <Link to="/privacy" data-testid="chrome-footer-privacy" className="hover:text-gold transition-colors">Privacy</Link>
              <span className="text-foreground/25">·</span>
              <Link to="/terms" data-testid="chrome-footer-terms" className="hover:text-gold transition-colors">Terms</Link>
              <span className="text-foreground/25">·</span>
              <span>© 2025 Habano App LLC</span>
            </div>
          </div>
        </footer>
      )}
      <LegalFooter />
      <TermsReacceptOverlay />
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <I18nProvider>
        <LocationPrimerProvider>
          <WhatsAppSheetProvider>
            <div className="App">
              <Toaster position="top-right" richColors closeButton />
              <ContactPopupHost />
              <BrowserRouter>
                <PreviewModeProvider>
                  <Shell />
                  {/* iter216 — Injects admin-authored custom code on `/` only. */}
                  <CustomCodeInjector />
                  <RideTrackingProvider />
                </PreviewModeProvider>
              </BrowserRouter>
            </div>
          </WhatsAppSheetProvider>
        </LocationPrimerProvider>
      </I18nProvider>
    </ErrorBoundary>
  );
}
